'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { BookOpen, Shuffle, Clock, Type, CheckCircle, Info, X, ChevronDown, ChevronUp, Send, RotateCcw, Lightbulb, Tag, BarChart2, Zap, BookMarked, Loader2, Search, SlidersHorizontal } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import { questionBank, topics, difficulties, IELTSQuestion, QuestionType, Difficulty } from '@/data/questions';
import Icon from '@/components/ui/AppIcon';


const typeColors: Record<string, 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'secondary'> = {
  'Opinion': 'primary',
  'Discussion': 'info',
  'Advantages & Disadvantages': 'success',
  'Problems & Solutions': 'warning',
  'Double Question': 'danger',
  'Direct Question': 'secondary',
  'Mixed': 'secondary',
};

const difficultyColors: Record<string, 'success' | 'warning' | 'danger'> = {
  'Foundation': 'success',
  'Intermediate': 'warning',
  'Advanced': 'danger',
};

const MIN_WORDS = 250;
const RECOMMENDED_WORDS = 280;
const TIMER_SECONDS = 40 * 60;

function countWords(text: string): number {
  return text.trim() === '' ? 0 : text.trim().split(/\s+/).filter(Boolean).length;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export default function EssayWritingContent() {
  const router = useRouter();

  const [selectedQuestion, setSelectedQuestion] = useState<IELTSQuestion>(questionBank[0]);
  const [essayText, setEssayText] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(TIMER_SECONDS);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerStarted, setTimerStarted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);

  // Filters
  const [filterType, setFilterType] = useState<QuestionType | 'All'>('All');
  const [filterTopic, setFilterTopic] = useState<string>('All');
  const [filterDifficulty, setFilterDifficulty] = useState<Difficulty | 'All'>('All');
  const [searchQuery, setSearchQuery] = useState('');

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const filteredQuestions = questionBank.filter((q) => {
    const matchType = filterType === 'All' || q.type === filterType;
    const matchTopic = filterTopic === 'All' || q.topic === filterTopic;
    const matchDiff = filterDifficulty === 'All' || q.difficulty === filterDifficulty;
    const matchSearch = searchQuery === '' || q.prompt.toLowerCase().includes(searchQuery.toLowerCase()) || q.topic.toLowerCase().includes(searchQuery.toLowerCase());
    return matchType && matchTopic && matchDiff && matchSearch;
  });

  useEffect(() => {
    if (timerRunning && timerSeconds > 0) {
      timerRef.current = setInterval(() => {
        setTimerSeconds((prev) => prev - 1);
      }, 1000);
    } else if (timerSeconds === 0) {
      setTimerRunning(false);
      toast.warning('Time is up! 40 minutes have elapsed.');
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timerRunning, timerSeconds]);

  const handleEssayChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setEssayText(text);
    setWordCount(countWords(text));
    if (!timerStarted && text.length > 0) {
      setTimerStarted(true);
      setTimerRunning(true);
    }
  }, [timerStarted]);

  const handleSelectQuestion = (q: IELTSQuestion) => {
    setSelectedQuestion(q);
    setEssayText('');
    setWordCount(0);
    setTimerSeconds(TIMER_SECONDS);
    setTimerRunning(false);
    setTimerStarted(false);
    setDrawerOpen(false);
    toast.success(`Question loaded: ${q.type} — ${q.topic}`);
  };

  const handleRandomQuestion = () => {
    const pool = filteredQuestions.length > 0 ? filteredQuestions : questionBank;
    const idx = Math.floor(Date.now() / 1000) % pool.length;
    handleSelectQuestion(pool[idx]);
  };

  const handleSubmit = async () => {
    if (wordCount < MIN_WORDS) {
      toast.error(`Your essay is ${wordCount} words. The minimum requirement is 250 words.`);
      return;
    }
    setIsSubmitting(true);
    setTimerRunning(false);
    // BACKEND INTEGRATION: POST /api/assess { questionId: selectedQuestion.id, essay: essayText }
    await new Promise((r) => setTimeout(r, 3200));
    setIsSubmitting(false);
    toast.success('Assessment complete — reviewing your results now.');
    router.push('/assessment-results');
  };

  const handleResetTimer = () => {
    setTimerSeconds(TIMER_SECONDS);
    setTimerRunning(false);
    setTimerStarted(false);
    toast.info('Timer reset to 40:00');
  };

  const wordCountStatus: 'under' | 'low' | 'good' = wordCount < MIN_WORDS ? 'under' : wordCount < RECOMMENDED_WORDS ? 'low' : 'good';
  const wordCountColorClass = wordCountStatus === 'under' ? 'text-danger' : wordCountStatus === 'low' ? 'text-warning' : 'text-success';
  const timerWarning = timerSeconds < 300;
  const timerCritical = timerSeconds < 60;

  const ieltsTips = [
    { id: 'tip-1', icon: CheckCircle, text: 'Address ALL parts of the task — partial answers are penalised under Task Response.' },
    { id: 'tip-2', icon: CheckCircle, text: 'Write at least 250 words — responses under 250 words are penalised.' },
    { id: 'tip-3', icon: CheckCircle, text: 'State your position clearly in the introduction and maintain it throughout.' },
    { id: 'tip-4', icon: CheckCircle, text: 'Use a wide range of vocabulary — avoid repeating the same words.' },
    { id: 'tip-5', icon: CheckCircle, text: 'Vary your sentence structures — use both simple and complex sentences.' },
    { id: 'tip-6', icon: CheckCircle, text: 'Write a clear conclusion that restates your position without introducing new ideas.' },
  ];

  return (
    <div className="relative flex h-[calc(100vh-64px)] overflow-hidden">
      {/* Question Drawer Overlay */}
      {drawerOpen && (
        <div
          className="absolute inset-0 z-30 bg-foreground/20 backdrop-blur-sm md:hidden"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Left Panel — Question */}
      <aside
        className={`
          ${drawerOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          absolute md:relative z-40 md:z-auto
          w-[360px] lg:w-[400px] xl:w-[440px] shrink-0
          h-full bg-card border-r flex flex-col
          transition-transform duration-300 ease-in-out
        `}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-muted/30">
          <div className="flex items-center gap-2">
            <BookMarked size={16} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">Question Bank</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">
              {questionBank.length}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleRandomQuestion}
              title="Load a random question"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-all duration-150 active:scale-95"
            >
              <Shuffle size={13} />
              Random
            </button>
            <button
              onClick={() => setDrawerOpen(false)}
              className="md:hidden p-1.5 rounded-md text-muted-foreground hover:bg-muted transition-all duration-150"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 py-3 border-b space-y-2.5">
          {/* Search */}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
            />
          </div>
          {/* Type filter */}
          <div className="flex gap-1.5 flex-wrap">
            {(['All', 'Opinion', 'Discussion', 'Advantages & Disadvantages', 'Problems & Solutions'] as const).map((t) => (
              <button
                key={`filter-type-${t}`}
                onClick={() => setFilterType(t as QuestionType | 'All')}
                className={`text-xs px-2.5 py-1 rounded-full font-medium transition-all duration-150 ${
                  filterType === t
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-secondary'
                }`}
              >
                {t === 'Advantages & Disadvantages' ? 'Adv/Dis' : t === 'Problems & Solutions' ? 'Prob/Sol' : t}
              </button>
            ))}
          </div>
          {/* Difficulty + Topic */}
          <div className="flex gap-2">
            <select
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value as Difficulty | 'All')}
              className="flex-1 text-xs px-2.5 py-1.5 rounded-lg border bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
            >
              <option value="All">All Levels</option>
              {difficulties.map((d) => (
                <option key={`diff-${d}`} value={d}>{d}</option>
              ))}
            </select>
            <select
              value={filterTopic}
              onChange={(e) => setFilterTopic(e.target.value)}
              className="flex-1 text-xs px-2.5 py-1.5 rounded-lg border bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
            >
              <option value="All">All Topics</option>
              {topics.map((t) => (
                <option key={`topic-${t}`} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <p className="text-xs text-muted-foreground">
            {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''} found
          </p>
        </div>

        {/* Question List */}
        <div className="flex-1 overflow-y-auto">
          {filteredQuestions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-center px-6">
              <BookOpen size={28} className="text-muted-foreground mb-2" />
              <p className="text-sm font-medium text-foreground">No questions match your filters</p>
              <p className="text-xs text-muted-foreground mt-1">Try adjusting the topic or difficulty filters above.</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredQuestions.map((q) => (
                <button
                  key={`q-item-${q.id}`}
                  onClick={() => handleSelectQuestion(q)}
                  className={`w-full text-left px-4 py-3.5 hover:bg-muted/50 transition-all duration-150 ${
                    selectedQuestion.id === q.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                  }`}
                >
                  <div className="flex items-start gap-2 mb-1.5">
                    <Badge variant={typeColors[q.type] || 'secondary'} size="sm">{q.type}</Badge>
                    <Badge variant={difficultyColors[q.difficulty]} size="sm">{q.difficulty}</Badge>
                  </div>
                  <p className="text-xs text-foreground leading-relaxed line-clamp-2 mb-1.5">
                    {q.prompt}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <Tag size={10} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{q.topic}</span>
                    {selectedQuestion.id === q.id && (
                      <span className="ml-auto text-xs font-semibold text-primary">Selected</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </aside>

      {/* Right Panel — Writing Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Writing Toolbar */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-card shrink-0">
          {/* Left: open drawer on mobile, question meta */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setDrawerOpen(true)}
              className="md:hidden flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-secondary-foreground bg-muted rounded-lg hover:bg-secondary transition-all duration-150"
            >
              <SlidersHorizontal size={13} />
              Questions
            </button>
            <div className="hidden md:flex items-center gap-2">
              <Badge variant={typeColors[selectedQuestion.type] || 'secondary'}>{selectedQuestion.type}</Badge>
              <Badge variant={difficultyColors[selectedQuestion.difficulty]}>{selectedQuestion.difficulty}</Badge>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Tag size={11} />
                {selectedQuestion.topic}
              </span>
            </div>
          </div>

          {/* Right: timer + word count */}
          <div className="flex items-center gap-3">
            {/* Word Count */}
            <div className={`flex items-center gap-1.5 font-mono-data text-sm font-semibold ${wordCountColorClass}`}>
              <Type size={14} />
              <span>{wordCount}</span>
              <span className="text-xs font-normal text-muted-foreground">/ 250 min</span>
            </div>

            {/* Timer */}
            <div className={`flex items-center gap-1.5 ${timerCritical ? 'timer-warning text-danger' : timerWarning ? 'text-warning' : 'text-muted-foreground'}`}>
              <Clock size={14} />
              <span className="font-mono-data text-sm font-semibold">{formatTime(timerSeconds)}</span>
              <div className="flex gap-0.5">
                <button
                  onClick={() => setTimerRunning(!timerRunning)}
                  className="px-2 py-1 text-xs rounded-md bg-muted hover:bg-secondary transition-all duration-150 font-medium"
                >
                  {timerRunning ? 'Pause' : timerStarted ? 'Resume' : 'Start'}
                </button>
                <button
                  onClick={handleResetTimer}
                  title="Reset timer"
                  className="p-1 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
                >
                  <RotateCcw size={13} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Writing Area */}
        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
          {/* Essay Textarea Column */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Question Prompt */}
            <div className="px-5 pt-4 pb-3 border-b bg-muted/20 shrink-0">
              <div className="max-w-3xl">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  IELTS Writing Task 2 — {selectedQuestion.examType}
                </p>
                <p className="text-sm text-foreground leading-relaxed mb-2.5">
                  {selectedQuestion.prompt}
                </p>
                <p className="text-sm font-semibold text-primary">
                  {selectedQuestion.taskInstruction}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Give reasons for your answer and include any relevant examples from your own knowledge or experience. Write at least 250 words.
                </p>
              </div>
            </div>

            {/* Textarea */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <textarea
                ref={textareaRef}
                value={essayText}
                onChange={handleEssayChange}
                placeholder="Begin writing your essay here. The timer will start automatically when you start typing.

Aim for 250–300 words. Address every part of the task instruction above."
                className="flex-1 w-full p-5 essay-textarea bg-card text-foreground placeholder:text-muted-foreground/50 border-0 focus:outline-none resize-none"
                spellCheck
                disabled={isSubmitting}
              />

              {/* Bottom bar */}
              <div className="flex items-center justify-between px-5 py-3 border-t bg-card shrink-0">
                <div className="flex items-center gap-4">
                  {/* Word count detail */}
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${wordCountStatus === 'under' ? 'bg-danger' : wordCountStatus === 'low' ? 'bg-warning' : 'bg-success'}`} />
                    <span className={`text-xs font-semibold ${wordCountColorClass}`}>
                      {wordCount} words
                    </span>
                    {wordCountStatus === 'under' && (
                      <span className="text-xs text-muted-foreground">
                        — {MIN_WORDS - wordCount} more needed
                      </span>
                    )}
                    {wordCountStatus === 'low' && (
                      <span className="text-xs text-muted-foreground">
                        — aim for {RECOMMENDED_WORDS}+
                      </span>
                    )}
                    {wordCountStatus === 'good' && (
                      <span className="text-xs text-success">
                        — minimum requirement met ✓
                      </span>
                    )}
                  </div>

                  {/* Word count progress bar */}
                  <div className="hidden sm:block w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        wordCountStatus === 'under' ? 'bg-danger' : wordCountStatus === 'low' ? 'bg-warning' : 'bg-success'
                      }`}
                      style={{ width: `${Math.min((wordCount / 300) * 100, 100)}%` }}
                    />
                  </div>
                </div>

                {/* Submit */}
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || wordCount < MIN_WORDS}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95 ${
                    wordCount >= MIN_WORDS && !isSubmitting
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm'
                      : 'bg-muted text-muted-foreground cursor-not-allowed'
                  }`}
                  style={{ minWidth: '160px' }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      <span>Assessing Essay…</span>
                    </>
                  ) : (
                    <>
                      <Send size={15} />
                      <span>Submit for Assessment</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Right sidebar — Tips + Criteria */}
          <aside className="hidden xl:flex flex-col w-72 2xl:w-80 border-l bg-muted/20 overflow-y-auto shrink-0">
            {/* Assessment Criteria */}
            <div className="px-4 py-4 border-b">
              <div className="flex items-center gap-2 mb-3">
                <BarChart2 size={14} className="text-primary" />
                <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Assessment Criteria</span>
              </div>
              <div className="space-y-2">
                {[
                  { code: 'TR', label: 'Task Response', desc: 'Address all parts of the task' },
                  { code: 'CC', label: 'Coherence & Cohesion', desc: 'Organisation and flow' },
                  { code: 'LR', label: 'Lexical Resource', desc: 'Vocabulary range and accuracy' },
                  { code: 'GRA', label: 'Grammatical Range & Accuracy', desc: 'Sentence variety and correctness' },
                ].map((c) => (
                  <div key={`criteria-${c.code}`} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-card border">
                    <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-primary">{c.code}</span>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-foreground">{c.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{c.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* IELTS Tips */}
            <div className="px-4 py-4">
              <button
                onClick={() => setTipsOpen(!tipsOpen)}
                className="w-full flex items-center justify-between mb-3"
              >
                <div className="flex items-center gap-2">
                  <Lightbulb size={14} className="text-warning" />
                  <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Examiner Tips</span>
                </div>
                {tipsOpen ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
              </button>
              {tipsOpen && (
                <div className="space-y-2 fade-in">
                  {ieltsTips.map((tip) => {
                    const Icon = tip.icon;
                    return (
                      <div key={tip.id} className="flex items-start gap-2 p-2.5 rounded-lg bg-card border">
                        <Icon size={13} className="text-success shrink-0 mt-0.5" />
                        <p className="text-xs text-secondary-foreground leading-relaxed">{tip.text}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Band score info */}
              <div className="mt-4 p-3 rounded-lg bg-info-bg border border-info-border">
                <div className="flex items-center gap-1.5 mb-2">
                  <Info size={13} className="text-info" />
                  <span className="text-xs font-semibold text-info">Band Score Weights</span>
                </div>
                <p className="text-xs text-secondary-foreground leading-relaxed">
                  Each of the four criteria (TR, CC, LR, GRA) contributes equally — 25% — to your overall band score. No single criterion is more important than another.
                </p>
              </div>

              {/* Submitting overlay tip */}
              {isSubmitting && (
                <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/20 fade-in">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap size={13} className="text-primary examiner-thinking" />
                    <span className="text-xs font-semibold text-primary">AI Examiner is reviewing…</span>
                  </div>
                  <div className="space-y-1.5">
                    {['Analysing Task Response…', 'Evaluating Coherence…', 'Assessing Vocabulary…', 'Checking Grammar…'].map((step, i) => (
                      <div key={`step-${i + 1}`} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary/40 examiner-thinking" style={{ animationDelay: `${i * 0.3}s` }} />
                        <span className="text-xs text-muted-foreground">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}